/**
 * Team Inbox — MULTI-SELECT forward orchestration (PURE, no React/Firebase).
 *
 * The single-message forward path reuses the SAME transports this module
 * loops: a message with a forwardable attachment (image/document) goes via
 * `forwardAttachment` (reusing the received bytes — no re-upload); a plain
 * TEXT message goes via `sendText`. This module is the thin layer ON TOP that
 * lets the owner forward SEVERAL received messages — text, images, PDFs, mixed
 * — to one target in a single gesture.
 *
 * The backend contract is strictly one-call-per-message (no bulk shape), so
 * this runs the forwards SEQUENTIALLY, reports progress per message, and —
 * critically — never loses one silently: every item ends up in `sent` or
 * `failed`, and a partial failure is surfaced with the exact names so the owner
 * can retry just the ones that did not go.
 *
 * Kept dependency-free (types only) so the Node test runner can exercise it.
 */
import type { ForwardableAttachment } from '../../../lib/messagesStore';

/** One message queued for forwarding — its message id (for selection
 *  reconcile), a human label (file name for media, or a short text snippet,
 *  used in progress + error copy), and BOTH possible payloads. Exactly one of
 *  `attachment` / `text` drives the send (attachment wins — see `routeForward`);
 *  the other is null. */
export interface ForwardItem {
  id: string;
  fileName: string;
  /** The forwardable attachment when this message carries a supported file
   *  (image/document with reachable bytes); null for a text-only forward. */
  attachment: ForwardableAttachment | null;
  /** The plain-text body when this is a text forward; null for a media forward. */
  text: string | null;
}

/** How ONE item will be sent — the pure routing decision (attachment wins over
 *  text; `none` when the item carries neither, which the runner treats as a
 *  captured failure so nothing is ever lost). */
export type ForwardRoute =
  | { via: 'attachment'; attachment: ForwardableAttachment }
  | { via: 'text'; text: string }
  | { via: 'none' };

/**
 * PURE — decide how a queued item forwards: a forwardable ATTACHMENT is sent as
 * a file (received bytes reused, no re-upload); otherwise a non-empty TEXT body
 * is sent as a plain message; an item with neither routes to `none`.
 */
export function routeForward(
  item: Pick<ForwardItem, 'attachment' | 'text'>,
): ForwardRoute {
  if (item.attachment) return { via: 'attachment', attachment: item.attachment };
  const t = (item.text || '').trim();
  if (t) return { via: 'text', text: t };
  return { via: 'none' };
}

/** The two ONE-message transports the loop routes to (both reuse the shipped
 *  single-message send callable — no fork). */
export interface ForwardTransport {
  /** Forward an already-received attachment (reuses stored bytes). */
  forwardAttachment: (attachment: ForwardableAttachment) => Promise<unknown>;
  /** Send a plain text message. */
  sendText: (text: string) => Promise<unknown>;
}

/** A file that could NOT be forwarded, with a plain-language reason. */
export interface ForwardFailure {
  id: string;
  fileName: string;
  reason: string;
}

/** The result of a multi-forward run — every input lands in exactly one list. */
export interface ForwardManyOutcome {
  /** ids of the files that forwarded OK. */
  sent: string[];
  /** files that failed (kept, with reason, so the caller can retry them). */
  failed: ForwardFailure[];
}

/**
 * Map a raw forward transport error to the owner-facing plain-language reason.
 * SHARED by the single- and multi-message paths so both speak the same copy.
 * `kind` tailors the 24h-window message (a file vs a plain message) — WhatsApp
 * blocks BOTH outside the window, but the honest wording differs.
 */
export function mapForwardError(
  e: unknown,
  kind: 'text' | 'media' = 'media',
): string {
  const msg = (e as { message?: string })?.message || '';
  if (/forward_nothing/.test(msg)) {
    return 'there was nothing to forward in this message';
  }
  if (/forward_unavailable:no_source/.test(msg)) {
    return (
      'the original is no longer reachable — ask the sender to share it again'
    );
  }
  if (/send_blocked:window_closed_media_blocked|send_blocked/.test(msg)) {
    return kind === 'text'
      ? "that chat's 24-hour window is closed, so WhatsApp won't accept a new " +
          'message there yet'
      : "that chat's 24-hour window is closed, so WhatsApp won't accept a file " +
          'there yet';
  }
  if (/unsupported_media_type/.test(msg)) {
    return "WhatsApp can't send this file type (convert it to PDF or PNG)";
  }
  return 'the send did not go through';
}

/**
 * Forward each item to ONE target, one call at a time (the backend requires a
 * single call per message). Each item is ROUTED to the right transport: a
 * forwardable attachment → `forwardAttachment` (bytes reused); a text body →
 * `sendText`. Reports progress BEFORE each send (`completed` items so far,
 * `total`) and once more when done. Never throws for a per-item failure — it is
 * captured in `failed` so nothing is lost; even an item that routes to `none`
 * (no attachment and no text — should not happen for a pre-filtered selection)
 * is recorded as a failure rather than dropped.
 */
export async function runForwardMany(
  items: ForwardItem[],
  transport: ForwardTransport,
  opts?: {
    onProgress?: (completed: number, total: number) => void;
    mapError?: (e: unknown, kind: 'text' | 'media') => string;
  },
): Promise<ForwardManyOutcome> {
  const total = items.length;
  const sent: string[] = [];
  const failed: ForwardFailure[] = [];
  const mapErr = opts?.mapError ?? mapForwardError;

  for (let i = 0; i < items.length; i++) {
    // `i` items are already done; the one about to send is #(i+1).
    opts?.onProgress?.(i, total);
    const it = items[i];
    const route = routeForward(it);
    try {
      if (route.via === 'attachment') {
        await transport.forwardAttachment(route.attachment);
      } else if (route.via === 'text') {
        await transport.sendText(route.text);
      } else {
        // Neither payload — treat as a captured failure (nothing lost).
        throw new Error('forward_nothing:empty');
      }
      sent.push(it.id);
    } catch (e) {
      failed.push({
        id: it.id,
        fileName: it.fileName,
        reason: mapErr(e, route.via === 'text' ? 'text' : 'media'),
      });
    }
  }
  opts?.onProgress?.(total, total);
  return { sent, failed };
}

/**
 * Compose the plain-language error block for a partial (or total) failure, or
 * `null` when every file went. Leads with what SUCCEEDED (nothing lost), names
 * the exact files that failed, and — when they all failed for the same reason
 * (e.g. the target window is closed) — states that reason once.
 */
export function forwardManyErrorText(
  outcome: ForwardManyOutcome,
  total: number,
): string | null {
  const { sent, failed } = outcome;
  if (failed.length === 0) return null;
  const names = failed.map((f) => f.fileName).join(', ');
  const okLead = sent.length > 0 ? `${sent.length} of ${total} sent. ` : '';
  const plural = failed.length === 1 ? '' : 's';
  const reasons = new Set(failed.map((f) => f.reason));
  const common = reasons.size === 1 ? `${[...reasons][0]}. ` : '';
  const retryWord = failed.length === 1 ? 'it' : 'them';
  return (
    `${okLead}Could not forward ${failed.length} message${plural}: ${names}. ` +
    `${common}Pick a chat to try ${retryWord} again.`
  );
}

/** Toggle one id in an immutable id list (add if absent, remove if present). */
export function toggleId(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}
